import { forwardRef, useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Loader2, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useTranslations } from 'next-intl';
import Image from 'next/image';

interface DropzoneProps {
  'value'?: string;
  'onChange'?: (image: File | null) => void;
  'disabled'?: boolean;
  'isLoading'?: boolean;
  'data-cy'?: string;
}

export const Dropzone = forwardRef<HTMLDivElement, DropzoneProps>(
  ({ 'value': initialValue, onChange, disabled = false, isLoading, 'data-cy': dataCy }, ref) => {
    const tWatermark = useTranslations('labels.watermark');
    const [value, setImage] = useState<string | null>(initialValue ?? null);

    const handleDrop = (acceptedFiles: Array<File>) => {
      if (acceptedFiles.length > 0) {
        const file = acceptedFiles[0];
        const fileUrl = URL.createObjectURL(file);
        setImage(fileUrl);
        onChange?.(file);
      }
    };

    useEffect(() => {
      if (typeof initialValue === 'string') {
        setImage(initialValue);
      } else if (initialValue === null) {
        setImage(null);
      }
    }, [initialValue]);

    const handleRemove = () => {
      setImage(null);
      onChange?.(null);
    };

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
      onDrop: handleDrop,
      accept: {
        'image/png': ['.png'],
      },
      multiple: false,
      disabled,
    });

    const { ref: dropzoneRef, ...rootProps } = getRootProps();

    return (
      <>
        <div
          {...rootProps}
          ref={(node) => {
            dropzoneRef.current = node;
            if (typeof ref === 'function') {
              ref(node);
            } else if (ref) {
              ref.current = node;
            }
          }}
          className={cn(
            value
              ? 'flex flex-col items-center space-y-2'
              : 'flex w-full cursor-pointer items-center justify-center rounded-[36px] border-[6px] border-dashed border-text-gray bg-transparent px-5 py-6 text-center text-sm text-text-gray transition duration-150 hover:border-neutral-400 focus:outline-hidden',
            isDragActive && 'border-neutral-500',
            disabled && 'cursor-not-allowed opacity-50',
          )}
          data-cy={dataCy}
        >
          {value ? (
            <div
              className={cn(
                'relative h-32 w-full border border-text-gray-alternate bg-ui-void-gap',
                !value && 'flex items-center justify-center',
              )}
            >
              <Image src={value} alt={tWatermark('preview')} fill className="object-contain" />
            </div>
          ) : (
            <>
              <input {...getInputProps()} />
              {isLoading ? (
                <Loader2 className="icon-4 animate-spin opacity-50" />
              ) : (
                <div className="flex flex-col items-center">
                  <Upload className="mb-2 icon-12" strokeWidth={1} />
                  <p className="text-balance">{tWatermark('drop')}</p>
                </div>
              )}
            </>
          )}
        </div>
        {value && (
          <Button
            onClick={handleRemove}
            variant="secondary"
            disabled={disabled || !value}
            className="mt-3 h-12 w-full"
            size="sm"
            data-cy={`${dataCy}-remove-button`}
          >
            {tWatermark('remove')}
          </Button>
        )}
      </>
    );
  },
);
Dropzone.displayName = 'Dropzone';
